import { memo, useMemo, useState } from "react";
import { toast } from "react-toastify";
import type { DocumentReference, Patient } from "fhir/r5";
import {
  createDocumentTransactionId,
  extractDocumentTransactionId,
  extractDocumentVerification,
} from "src-next/modules/patient-supporting-documents";
import { sortDocumentsByDateDesc } from "../../helpers/documentDisplay";
import {
  filterCategoriesForTransaction,
  resolveCategoryStatus,
} from "../../helpers/documentsChecklistHelpers";
import { useDocumentAttestationActions } from "../../hooks/useDocumentAttestationActions";
import type { DocumentCategory } from "../../types";
import { DocumentsChecklistSection } from "./DocumentsChecklistSection";
import type { RejectTarget } from "./DocumentTile";
import { RejectDocumentDialog } from "./RejectDocumentDialog";
import {
  ApproveDocumentDialog,
  type ApproveDocumentTarget,
} from "./ApproveDocumentDialog";
import { RevokeConfirmDialog } from "./RevokeConfirmDialog";

interface DocumentsChecklistProps {
  categories: DocumentCategory[];
  organizationId?: string;
  practitionerRoleId?: string;
  patient?: Patient | null;
  transactionId?: string;
  interactionDisabled?: boolean;
  onSendWhatsApp?: (
    patients: Patient[],
    options?: { onSent?: () => void },
  ) => void;
  onView: (doc: DocumentReference, title: string) => void;
  onDocumentsChanged: () => void;
}

function resolveApproveTransactionId(
  doc: DocumentReference,
  categories: DocumentCategory[],
  fallbackTransactionId?: string,
): string | undefined {
  const existing = extractDocumentTransactionId(doc);
  if (existing) return existing;
  if (
    fallbackTransactionId &&
    !fallbackTransactionId.startsWith("pending:")
  ) {
    return fallbackTransactionId;
  }

  const code =
    doc.category?.[0]?.coding?.[0]?.code || doc.category?.[0]?.text;
  const isTxDoc =
    code === "medical_prescription" || code === "anvisa_import_authorization";
  if (!isTxDoc) return undefined;

  const siblingCode =
    code === "medical_prescription"
      ? "anvisa_import_authorization"
      : "medical_prescription";
  const sibling = categories
    .find((category) => category.code === siblingCode)
    ?.docs.find((item) => extractDocumentTransactionId(item));
  return extractDocumentTransactionId(sibling) || createDocumentTransactionId();
}

export const DocumentsChecklist = memo(function DocumentsChecklist({
  categories,
  organizationId,
  practitionerRoleId,
  patient,
  transactionId,
  interactionDisabled = false,
  onSendWhatsApp,
  onView,
  onDocumentsChanged,
}: DocumentsChecklistProps) {
  const [busyDocId, setBusyDocId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveTarget, setApproveTarget] =
    useState<ApproveDocumentTarget | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{
    kind: "approval" | "rejection";
    doc: DocumentReference;
    title: string;
  } | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);

  const { runAttest, runAttestAll, runReject, runRevokeApproval, runRevokeRejection } =
    useDocumentAttestationActions();

  const scoped = useMemo(
    () => filterCategoriesForTransaction(categories, transactionId),
    [categories, transactionId],
  );

  const inheritedApprovedIds = useMemo(() => {
    return scoped.inheritedDocIds.filter((id) => {
      const doc = scoped.categories
        .flatMap((category) => category.docs)
        .find((item) => item.id === id);
      return (
        extractDocumentVerification(doc, organizationId).status === "approved"
      );
    });
  }, [scoped, organizationId]);

  const rows = useMemo(
    () =>
      scoped.categories.map((category) => {
        const docs = sortDocumentsByDateDesc(
          category.docs.filter((document) => document.id),
        );
        return {
          category,
          docs,
          status: resolveCategoryStatus(docs, organizationId),
        };
      }),
    [scoped.categories, organizationId],
  );

  const pendingDocs = useMemo(
    () =>
      rows.flatMap((row) =>
        row.docs.filter((document) => {
          if (document.id && inheritedApprovedIds.includes(document.id)) {
            return false;
          }
          return (
            extractDocumentVerification(document, organizationId).status ===
            "pending"
          );
        }),
      ),
    [rows, organizationId, inheritedApprovedIds],
  );

  const canVerify = Boolean(
    organizationId?.trim() && practitionerRoleId?.trim(),
  );
  const canVerifyActions = canVerify && !interactionDisabled;

  const persistApproval = async (
    doc: DocumentReference,
    label: string,
    validUntil: string,
  ) => {
    if (!canVerifyActions || !doc.id) {
      toast.error("Não foi possível identificar o documento ou a loja.");
      return;
    }
    setBusyDocId(doc.id);
    try {
      await runAttest({
        documentReferenceId: doc.id,
        organizationId: organizationId!.trim(),
        practitionerRoleId: practitionerRoleId!,
        currentAttester: doc.attester,
        currentExtension: doc.extension,
        validUntil,
        transactionId: resolveApproveTransactionId(
          doc,
          scoped.categories,
          transactionId,
        ),
      });
      toast.success(`${label} verificado.`);
      setApproveTarget(null);
      onDocumentsChanged();
    } catch {
      toast.error(`Não foi possível verificar ${label}.`);
    } finally {
      setBusyDocId(null);
    }
  };

  const approveAll = async () => {
    if (!canVerifyActions || !pendingDocs.length) return;
    const first = pendingDocs[0];
    if (!first) return;
    setApproveTarget({
      doc: first,
      title: first.description?.trim() || "Documentos pendentes",
      categoryLabel: `Aprovar todos (${pendingDocs.length})`,
    });
  };

  const confirmApprove = async (validUntil: string) => {
    if (approveTarget?.categoryLabel.startsWith("Aprovar todos")) {
      if (!canVerifyActions) return;
      setApprovingAll(true);
      try {
        const sharedTx = createDocumentTransactionId();
        const result = await runAttestAll({
          organizationId: organizationId!.trim(),
          practitionerRoleId: practitionerRoleId!,
          validUntil,
          documents: pendingDocs.map((document) => ({
            documentReferenceId: document.id as string,
            currentAttester: document.attester,
            currentExtension: document.extension,
            transactionId: resolveApproveTransactionId(
              document,
              scoped.categories,
              transactionId ?? sharedTx,
            ),
          })),
        });
        if (result.failed.length) {
          toast.error(`Falha em ${result.failed.length} documento(s).`);
        } else {
          toast.success("Documentos verificados.");
        }
      } catch {
        toast.error("Não foi possível aprovar.");
      } finally {
        setApprovingAll(false);
        setApproveTarget(null);
        onDocumentsChanged();
      }
      return;
    }
    if (!approveTarget) return;
    await persistApproval(approveTarget.doc, approveTarget.title, validUntil);
  };

  const closeReject = () => {
    setRejectTarget(null);
    setRejectReason("");
  };

  const rejectDoc = async (options?: { sendWhatsApp?: boolean }) => {
    const doc = rejectTarget?.doc;
    if (!canVerifyActions) return;
    if (!rejectReason.trim() || !doc?.id || !practitionerRoleId) {
      toast.error("Informe o motivo da reprovação.");
      return;
    }
    if (!organizationId?.trim()) {
      toast.error("Organização não identificada para reprovação.");
      return;
    }
    setBusyDocId(doc.id);
    try {
      await runReject({
        documentReferenceId: doc.id,
        reason: rejectReason.trim(),
        practitionerRoleId,
        organizationId: organizationId.trim(),
        currentExtension: doc.extension,
      });
      toast.success("Documento reprovado.");
      closeReject();
      onDocumentsChanged();
      if (options?.sendWhatsApp && patient && onSendWhatsApp) {
        onSendWhatsApp([patient]);
      }
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível reprovar.",
      );
    } finally {
      setBusyDocId(null);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget?.doc.id || !organizationId?.trim()) return;
    setBusyDocId(revokeTarget.doc.id);
    try {
      if (revokeTarget.kind === "approval") {
        await runRevokeApproval({
          documentReferenceId: revokeTarget.doc.id,
          organizationId: organizationId.trim(),
          currentAttester: revokeTarget.doc.attester,
        });
        toast.success("Aprovação desfeita.");
      } else {
        await runRevokeRejection({
          documentReferenceId: revokeTarget.doc.id,
          organizationId: organizationId.trim(),
          currentExtension: revokeTarget.doc.extension,
        });
        toast.success("Recusa desfeita.");
      }
      setRevokeTarget(null);
      onDocumentsChanged();
    } catch {
      toast.error("Não foi possível desfazer.");
    } finally {
      setBusyDocId(null);
    }
  };

  return (
    <>
      <DocumentsChecklistSection
        rows={rows}
        pendingCount={pendingDocs.length}
        canVerifyActions={canVerifyActions}
        organizationId={organizationId}
        busyDocId={busyDocId}
        approvingAll={approvingAll}
        inheritedDocIds={inheritedApprovedIds}
        onView={onView}
        onApprove={(doc, title) =>
          setApproveTarget({
            doc,
            title,
            categoryLabel:
              rows.find((row) => row.docs.some((item) => item.id === doc.id))
                ?.category.label ?? "Documento",
          })
        }
        onReject={setRejectTarget}
        onApproveAll={() => void approveAll()}
        onRevokeApproval={(doc, title) =>
          setRevokeTarget({ kind: "approval", doc, title })
        }
        onRevokeRejection={(doc, title) =>
          setRevokeTarget({ kind: "rejection", doc, title })
        }
      />

      {approveTarget ? (
        <ApproveDocumentDialog
          target={approveTarget}
          busy={Boolean(busyDocId) || approvingAll}
          onClose={() => setApproveTarget(null)}
          onConfirm={(validUntil) => void confirmApprove(validUntil)}
        />
      ) : null}

      {rejectTarget ? (
        <RejectDocumentDialog
          target={rejectTarget}
          rejectReason={rejectReason}
          busyDocId={busyDocId}
          showWhatsApp={Boolean(patient && onSendWhatsApp)}
          onReasonChange={setRejectReason}
          onConfirm={() => void rejectDoc()}
          onConfirmAndWhatsApp={() => void rejectDoc({ sendWhatsApp: true })}
          onClose={closeReject}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeConfirmDialog
          title={
            revokeTarget.kind === "approval"
              ? "Desfazer aprovação?"
              : "Desfazer recusa?"
          }
          description={
            revokeTarget.kind === "approval"
              ? `A aprovação de “${revokeTarget.title}” nesta loja será removida.`
              : `A recusa de “${revokeTarget.title}” nesta loja será removida.`
          }
          busy={Boolean(busyDocId)}
          onClose={() => setRevokeTarget(null)}
          onConfirm={() => void confirmRevoke()}
        />
      ) : null}
    </>
  );
});
